/**
 * Ответственный по абонентскому обслуживанию по обучению (training-subscription-responsible,
 * постановка заказчицы 13.10.2026): заказ обучения клиента на обслуживании получает ответственным
 * выбранную здесь учётную запись («Учебный центр») — автоматически, при отправке на проверку.
 * Выбирает администратор в разделе «Аккаунты».
 */
import { useEffect, useState } from 'react'
import { teamApi, type ClientManager, type StaffMember } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Badge } from '@/design-system/components/primitives/badge'
import { Hint } from '@/design-system/components/primitives/text'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { staffOptions } from '@/team/use-staff'

/** Значение пункта «Не задан»: у Radix Select пустая строка зарезервирована. */
const NONE = '__none__'

/** Подпись результата сохранения: в скольких уже отправленных заказах проставлен ответственный. */
export function assignedOrdersText(count: number): string {
  const single = count % 10 === 1 && count % 100 !== 11
  return single
    ? `Ответственный проставлен в ${count} уже отправленном заказе`
    : `Ответственный проставлен в ${count} уже отправленных заказах`
}

/**
 * `staff` — действующие сотрудники Центрики. Список приходит от страницы «Аккаунты», а не из
 * справочника: только что созданная учётная запись («Учебный центр») должна быть доступна для
 * выбора сразу, без перезагрузки страницы.
 */
export function TrainingSubscriptionResponsibleCard({
  staff,
}: {
  staff: Pick<StaffMember, 'id' | 'fullName' | 'email'>[]
}) {
  const [responsible, setResponsible] = useState<ClientManager | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [assigned, setAssigned] = useState<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    teamApi
      .getTrainingSubscriptionResponsible(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setResponsible(res.responsible)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить ответственного'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  async function change(value: string) {
    if (saving) return
    setError(null)
    setAssigned(null)
    setSaving(true)
    try {
      const res = await teamApi.setTrainingSubscriptionResponsible(value === NONE ? '' : value)
      setResponsible(res.responsible)
      setAssigned(res.assignedOrders ?? 0)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить ответственного'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card header="Ответственный по абонентскому обслуживанию по обучению" className="mb-4">
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="w-full max-w-narrow">
          <Select
            value={responsible?.id ?? NONE}
            onValueChange={(v) => void change(v)}
            disabled={loading || saving}
          >
            <SelectTrigger aria-label="Ответственный по абонентскому обслуживанию по обучению">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Не задан</SelectItem>
              {staffOptions(staff, responsible ?? undefined).map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {responsible?.disabled ? <Badge variant="gray">Учётная запись отключена</Badge> : null}
        {assigned !== null && assigned > 0 ? (
          <span className="text-13 text-txt-secondary">{assignedOrdersText(assigned)}</span>
        ) : null}
      </div>
      <Hint className="mt-2">
        {responsible?.disabled
          ? 'Пока учётная запись отключена, заказы остаются без ответственного — выберите действующего сотрудника.'
          : 'Заказ обучения, отправленный клиентом в срок договора абонентского обслуживания, автоматически получает этого сотрудника ответственным. В остальных заказах ответственного назначает руководитель.'}
      </Hint>
    </Card>
  )
}
